import { useEffect, useState, useMemo } from "react";
import { CircleNotch, MagnifyingGlass, Buildings } from "@phosphor-icons/react";
import { repApi } from "@/lib/repApi";

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

const STATUS_DOT = {
  active:          "bg-emerald-400",
  trial:           "bg-amber-400",
  past_due:        "bg-rose-400",
  suspended:       "bg-rose-400",
  cancelled:       "bg-slate-500",
  pending_payment: "bg-slate-500",
};

const STATUS_LABEL = {
  active:          "Ativo",
  trial:           "Trial",
  past_due:        "Atrasado",
  suspended:       "Suspenso",
  cancelled:       "Cancelado",
  pending_payment: "Aguardando pagamento",
};

export default function RepClients() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    repApi.get("/rep/clients").then(({ data }) => setItems(data));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) => (t.company_name || "").toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
          Portal do Representante
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">
          Meus clientes
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          {items === null
            ? "Carregando…"
            : `${items.length} ${items.length === 1 ? "cliente atribuído" : "clientes atribuídos"} a você.`}
        </p>
      </header>

      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 flex items-center gap-2 mb-4">
        <MagnifyingGlass size={14} weight="regular" className="text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome da empresa…"
          data-testid="rep-clients-search"
          className="bg-transparent flex-1 text-sm text-white placeholder:text-slate-600 focus:outline-none"
        />
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-left">
            <tr>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Empresa</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Plano</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Mensalidade</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Cadastro</th>
              <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Ativação</th>
            </tr>
          </thead>
          <tbody>
            {filtered === null && (
              <tr><td colSpan="6" className="p-12 text-center">
                <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
              </td></tr>
            )}
            {filtered?.length === 0 && (
              <tr><td colSpan="6" className="p-12 text-center text-slate-500 text-sm">
                <Buildings size={20} weight="regular" className="inline-block mb-2 text-slate-600" />
                <div>
                  {items?.length === 0
                    ? "Você ainda não possui clientes atribuídos."
                    : "Nenhum cliente encontrado com este filtro."}
                </div>
              </td></tr>
            )}
            {filtered?.map((t) => (
              <tr
                key={t.tenant_id}
                className="border-t border-white/5"
                data-testid={`rep-client-row-${t.tenant_id}`}
              >
                <td className="px-4 py-3">
                  <div className="text-white font-medium">{t.company_name}</div>
                  {t.phone && <div className="text-[11px] text-slate-500">{t.phone}</div>}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-300">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[t.status] || "bg-slate-500"}`} />
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-300 text-[12px]">
                  {t.plan_code || "—"}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {t.monthly_amount_cents > 0 ? (
                    <span className={t.status === "active" ? "text-emerald-400" : "text-slate-400"}>
                      {fmtBRL(t.monthly_amount_cents)}
                    </span>
                  ) : (
                    <span className="text-slate-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-400 text-[12px]">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3 text-slate-400 text-[12px]">{fmtDate(t.activated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
